import { CodeIcon, EyeIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { MermaidDiagram, type MermaidRenderState } from "./mermaid-diagram";

interface MermaidBlockProps {
  /** Raw Mermaid source from the code fence. */
  code: string;
}

/**
 * A Mermaid code block for static (non-editor) rich-text surfaces: renders
 * the diagram by default with a toggle back to the source, and keeps the
 * source visible whenever rendering fails (CR-21).
 */
export function MermaidBlock({ code }: MermaidBlockProps) {
  const [showSource, setShowSource] = useState(false);
  const [renderState, setRenderState] = useState<MermaidRenderState>("loading");

  const trimmed = code.trim();

  // An empty mermaid fence stays a plain code block — nothing to render.
  if (!trimmed) {
    return (
      <pre>
        <code className="language-mermaid">{code}</code>
      </pre>
    );
  }

  const sourceVisible = showSource || renderState === "error";

  return (
    <div data-type="mermaid-block" className="my-2 space-y-1">
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className="text-zinc-500"
          onClick={() => setShowSource((prev) => !prev)}
        >
          {showSource ? (
            <>
              <EyeIcon /> Show diagram
            </>
          ) : (
            <>
              <CodeIcon /> Show code
            </>
          )}
        </Button>
      </div>
      {!showSource && <MermaidDiagram code={trimmed} onRenderStateChange={setRenderState} />}
      {!!sourceVisible && (
        <pre>
          <code className="language-mermaid">{code}</code>
        </pre>
      )}
    </div>
  );
}
